import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssbuy.info';

export default function GuiaDeComprasPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pageUrl = `${siteUrl}/guia-de-compras`;

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Guia de Compras CSSBuy',
    description: 'Tutoriais completos, comparações e dicas para aproveitar ao máximo o CSSBuy Spreadsheet. Do iniciante ao avançado, temos conteúdo para todos os níveis.',
    url: pageUrl,
    hasPart: articles.map((article, idx) => ({
      '@type': 'Article',
      headline: article.title,
      description: article.description,
      url: `${siteUrl}/artigo/${article.slug}`,
      image: article.image,
      position: idx + 1,
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Início',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Guia de Compras',
      },
    ],
  };

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full bg-background-50 pt-24 md:pt-32 pb-12 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            CSSBuy Info
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-tight mb-6">
            Guia de <span className="text-accent-500">Compras</span>
          </h1>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Tutoriais completos, comparações e dicas para aproveitar ao máximo o CSSBuy Spreadsheet. Do iniciante ao avançado, temos conteúdo para todos os níveis.
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="relative w-full bg-background-50 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {articles.map((article, idx) => (
              <a
                key={article.slug}
                href={`/artigo/${article.slug}`}
                className="group flex flex-col bg-background-100 rounded-2xl border border-background-200/70 hover:border-accent-300 overflow-hidden transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full aspect-[16/10] bg-background-200 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading={idx < 6 ? 'eager' : 'lazy'}
                  />
                  <div className="absolute top-3 left-3">
                    <span className="inline-block bg-accent-500 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i> {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i> {article.readTime}
                    </span>
                  </div>
                  <h3 className="text-foreground-950 font-heading font-bold text-base md:text-lg leading-tight mb-2 group-hover:text-accent-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed line-clamp-3 mb-4 flex-1">
                    {article.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-accent-500 text-xs font-medium group-hover:gap-2 transition-all">
                    Ler artigo
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              </a>
            ))}
          </div>

          {/* Back to Home */}
          <div className="text-center mt-12">
            <a
              href="/"
              className="inline-flex items-center gap-2 bg-accent-500 text-white font-semibold px-6 py-3 rounded-full text-sm hover:bg-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar para Página Inicial
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}